import type { MachineProfile } from '../../types'

export const CONTROLLERS = [
  'Fanuc 0i',
  'Fanuc 30i',
  'Haas NGC',
  'Haas Classic',
  'Siemens 840D',
  'Mazak',
]

export const MATERIALS = [
  'AL 6061',
  'AL 7075',
  'Steel 1018',
  'Steel 4140',
  'SS 316',
  'Titanium Ti-6Al-4V',
  'PEEK',
]

interface MachineSelectorProps {
  profiles: MachineProfile[]
  selectedId: string | null
  onSelect: (id: string) => void
  onManageProfiles: () => void
  onCreateDemo?: () => void
}

export default function MachineSelector({
  profiles,
  selectedId,
  onSelect,
  onManageProfiles,
  onCreateDemo,
}: MachineSelectorProps) {
  const selected = profiles.find(p => p.id === selectedId) ?? null

  const selectClass = `
    w-full bg-white/5 border border-cyan-500/20 rounded-lg
    font-mono text-sm text-slate-200 px-3 py-2.5
    appearance-none cursor-pointer
    transition-all duration-200
    focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30
    focus:shadow-[0_0_12px_rgba(6,182,212,0.25)]
    hover:border-cyan-500/40
  `

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === '__manage__') {
      onManageProfiles()
      return
    }
    onSelect(e.target.value)
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Profile picker */}
      <div>
        <label className="block font-mono text-xs text-slate-500 mb-1.5 tracking-widest uppercase">
          Machine Profile
        </label>
        <div className="relative">
          <select
            value={selectedId ?? ''}
            onChange={handleChange}
            className={selectClass}
          >
            <option value="" disabled className="bg-[#0d1526] text-slate-500">
              — select machine —
            </option>
            {profiles.map(p => (
              <option key={p.id} value={p.id} className="bg-[#0d1526] text-slate-200">
                {p.name} — {p.controller}
              </option>
            ))}
            <option value="__manage__" className="bg-[#0d1526] text-cyan-400">
              ⚙ Manage Profiles...
            </option>
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-cyan-500/60">
            ▾
          </div>
        </div>
      </div>

      {/* Read-only summary card */}
      {selected && (
        <div className="border border-cyan-500/10 rounded-lg px-3 py-2.5 bg-white/2 font-mono text-[10px] text-slate-500 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-slate-400 font-semibold">{selected.name}</span>
            <span className="text-cyan-500/70">{selected.controller}</span>
          </div>
          <div className="flex items-center gap-4 text-slate-600">
            <span>{selected.travelX}×{selected.travelY}×{selected.travelZ} mm</span>
            <span>{selected.maxRpm.toLocaleString()} rpm</span>
          </div>
        </div>
      )}

      {profiles.length === 0 && (
        <div className="flex flex-col gap-2">
          {onCreateDemo && (
            <button
              type="button"
              onClick={onCreateDemo}
              className="
                font-mono text-xs text-cyan-300 text-center py-2
                border border-cyan-500/30 rounded-lg bg-cyan-500/5
                hover:border-cyan-400 hover:bg-cyan-500/10 transition-all duration-150
              "
            >
              Use demo setup (Haas VF-2, 3-axis mill)
            </button>
          )}
          <button
            type="button"
            onClick={onManageProfiles}
            className="
              font-mono text-xs text-slate-400 text-center py-2
              border border-dashed border-cyan-500/20 rounded-lg
              hover:border-cyan-500/40 hover:text-slate-300 transition-all duration-150
            "
          >
            + Create custom profile
          </button>
        </div>
      )}
    </div>
  )
}
